import { useCallback, useEffect, useState } from 'react';
import {
  useSubscription,
  DocumentNode,
  OnDataOptions,
  SubscriptionHookOptions,
  OperationVariables,
} from '@apollo/client';

export interface UseSubscriptionWithReconnectOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> extends Omit<
    SubscriptionHookOptions<TData, TVariables>,
    'onData' | 'onError' | 'skip'
  > {
  skip?: boolean;
  onData?: (data: OnDataOptions<TData>) => void;
  onError?: (error: any) => void;
  onReconnect?: () => void;
  onReconnectSuccess?: () => void;
  maxDelay?: number;
  initialDelay?: number;
  enableAutoReconnect?: boolean;
}

/**
 * Custom hook that wraps Apollo's useSubscription with automatic reconnection logic
 * Features:
 * - Automatic reconnection on network errors
 * - Exponential backoff for retry attempts
 * - Customizable delays and callbacks
 * - Connection state tracking
 *
 * @param subscription - GraphQL subscription document
 * @param options - Configuration options including standard Apollo options plus reconnection settings
 * @returns Object containing loading state, error, data, and manual reconnect function
 */
export function useSubscriptionWithReconnect<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  subscription: DocumentNode,
  options?: UseSubscriptionWithReconnectOptions<TData, TVariables>
) {
  const {
    skip = false,
    onData,
    onError,
    onReconnect,
    onReconnectSuccess,
    maxDelay = 10000, // Max 10 seconds
    initialDelay = 500, // Start with 500ms
    enableAutoReconnect = true,
    ...apolloOptions
  } = options || {};

  // State for reconnection logic
  const [errorCount, setErrorCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [shouldConnect, setShouldConnect] = useState(!skip);
  const [lastError, setLastError] = useState<any>(null);

  // Handle subscription errors with automatic reconnection
  const handleError = useCallback(
    (err: any) => {
      console.error('Subscription error:', err);
      setLastError(err);
      setIsConnected(false);

      // Call user's error handler if provided
      if (onError) {
        onError(err);
      }

      // Check if this is a network error that requires reconnection
      const isNetworkError =
        err?.networkError ||
        err?.message?.includes('WebSocket') ||
        err?.message?.includes('connection') ||
        err?.message?.includes('Network');

      if (enableAutoReconnect && isNetworkError) {
        console.log('Network error detected, attempting to reconnect...');
        setShouldConnect(false);

        // Exponential backoff: initialDelay * (2 ^ errorCount), capped at maxDelay
        const delay = Math.min(
          initialDelay * Math.pow(2, errorCount),
          maxDelay
        );

        setTimeout(() => {
          console.log(`Reconnecting subscription after ${delay}ms delay...`);
          if (onReconnect) {
            onReconnect();
          }
          setShouldConnect(true);
          setErrorCount((prev) => prev + 1);
        }, delay);
      }
    },
    [
      errorCount,
      initialDelay,
      maxDelay,
      enableAutoReconnect,
      onError,
      onReconnect,
    ]
  );

  // Handle successful data reception
  const handleData = useCallback(
    (data: OnDataOptions<TData>) => {
      // Reset error count on successful data reception
      if (errorCount > 0) {
        console.log('Subscription reconnected successfully');
        setErrorCount(0);
        if (onReconnectSuccess) {
          onReconnectSuccess();
        }
      }

      setIsConnected(true);
      setLastError(null);

      // Call user's data handler if provided
      if (onData) {
        onData(data);
      }
    },
    [errorCount, onData, onReconnectSuccess]
  );

  // Use Apollo's useSubscription with our handlers
  const result = useSubscription<TData, TVariables>(subscription, {
    ...apolloOptions,
    skip: skip || !shouldConnect,
    onData: handleData,
    onError: handleError,
    shouldResubscribe: true,
  });

  // Initialize connection when skip changes
  useEffect(() => {
    if (!skip) {
      setShouldConnect(true);
    }
  }, [skip]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setShouldConnect(false);
      setErrorCount(0);
      setIsConnected(false);
      setLastError(null);
    };
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    console.log('Manual reconnection triggered');
    setErrorCount(0);
    setShouldConnect(false);
    setTimeout(() => {
      setShouldConnect(true);
    }, 100);
  }, []);

  return {
    ...result,
    isConnected,
    reconnectAttempts: errorCount,
    lastError,
    reconnect,
  };
}

/**
 * Configuration helper for common subscription patterns
 */
export const createSubscriptionConfig = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  baseConfig: UseSubscriptionWithReconnectOptions<TData, TVariables>
): UseSubscriptionWithReconnectOptions<TData, TVariables> => {
  return {
    fetchPolicy: 'network-only',
    enableAutoReconnect: true,
    initialDelay: 500,
    maxDelay: 10000,
    ...baseConfig,
  };
};
